/**
 * assets/blankwall.js
 * Initializes the Blankwall AR ("View in Your Room") widget on Shopify product pages.
 * Adapted from the WooCommerce blankwall-snippet.js for this theme.
 *
 * Responsibilities:
 *  - Loads the Blankwall UMD library from CloudFront.
 *  - Creates #blankwall-container, img.product-image, .product-description.
 *  - Calls window.Blankwall.init() once prerequisites are ready.
 *  - Keeps the hidden image and selected dimensions in sync.
 *  - Bridges Shopify variant changes → localStorage['spp:selected-size'].
 */
(function () {
  'use strict';

  var SCRIPT_ID = 'blankwall-umd-cjs';
  var BLANKWALL_SRC = 'https://dfv2bs0cn166o.cloudfront.net/blankwall.umd.cjs';
  var SIZE_LS_KEY = 'spp:selected-size';
  var SIZE_BRIDGE_ATTR = 'data-selected-size';
  var DEBUG_PREFIX = '[Blankwall]';
  // Flip to true to re-enable our diagnostic logging.
  var DEBUG = false;

  function debug() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(DEBUG_PREFIX);
    console.info.apply(console, args);
  }

  // Clear localStorage size on page load to prevent a stale size persisting
  // across product navigations.
  try {
    localStorage.removeItem(SIZE_LS_KEY);
    debug('cleared localStorage size on page load');
  } catch (e) {}

  // ── Shopify variant change bridge ──────────────────────────────────────────
  // Subscribe to the theme's variantChange pub/sub and write the new variant
  // title (e.g. "50x70 cm") to localStorage so the size-sync interval picks
  // it up on the next tick.
  function bindVariantSizeBridge() {
    var subscribeFn = window.subscribe;
    var events = window.theme && window.theme.PUB_SUB_EVENTS;
    if (!subscribeFn || !events || !events.variantChange) return;
    subscribeFn(events.variantChange, function (payload) {
      var variant = payload && payload.data && payload.data.variant;
      if (!variant || !variant.title) return;
      try {
        localStorage.setItem(SIZE_LS_KEY, variant.title);
        debug('variantChange: size updated to', variant.title);
      } catch (e) {}
    });
  }

  // ── Load the Blankwall UMD bundle ──────────────────────────────────────────
  function loadBlankwall(cb) {
    debug('loadBlankwall: start');
    if (document.getElementById(SCRIPT_ID)) return cb();
    var s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = BLANKWALL_SRC;
    s.onload = function () {
      debug('loadBlankwall: script loaded');
      cb();
    };
    document.head.appendChild(s);
  }

  // ── Image URL normalization ────────────────────────────────────────────────
  // Removes Shopify/WordPress dimension suffixes (-300x400) and query params
  // so Blankwall receives a clean, original-size URL.
  function normalizeImageUrl(url) {
    if (!url) return '';
    url = url.replace(/-\d+x\d+(?=\.\w+$)/, '');
    // Also strip Shopify _NNNx image variants: image_800x.jpg → image.jpg
    url = url.replace(/_\d+x(?=\.\w+$)/, '');
    try {
      var u = new URL(url, location.origin);
      u.search = '';
      return u.toString();
    } catch (_) {
      return url;
    }
  }

  // ── Product image detection ────────────────────────────────────────────────
  // Tries the currently-active product slide first, then falls back to any
  // visible product photo.
  function firstHeroImage() {
    var selectors = [
      '.product__slide.media--active .product__photo img',
      '.product__slide:not(.media--hidden) .product__photo img',
      '.product-single__photos .product__photo img',
      '.product__slides .product__photo img'
    ];
    var el = null;
    for (var i = 0; i < selectors.length; i++) {
      el = document.querySelector(selectors[i]);
      if (el) break;
    }
    if (!el) return null;
    return {
      src: el.currentSrc || el.src || el.getAttribute('src') || '',
      width:
        el.naturalWidth ||
        el.width ||
        Math.round(el.getBoundingClientRect().width),
      height:
        el.naturalHeight ||
        el.height ||
        Math.round(el.getBoundingClientRect().height),
      element: el,
      source: 'dom'
    };
  }

  // ── Dimension default from image ratio ────────────────────────────────────
  function getDimensionDefault() {
    var hero = firstHeroImage();
    var w = hero && hero.width ? hero.width : 0;
    var h = hero && hero.height ? hero.height : 0;
    if (w && h) {
      if (Math.abs(w - h) <= 2) return '50x50 cm';
      if (w > h) return '70x50 cm';
      return '50x70 cm';
    }
    return '50x70 cm';
  }

  // ── Size normalization ──────────────────────────────────────────────────
  // Extracts a clean "WxH cm" from whatever string we're handed — a bare
  // variant title ("50x100", "40×50 cm"), an A-size wrapper ("A4 (21x29,7 cm)")
  // or a multi-option title ("50x100 cm / Black"). Returns '' when there's no
  // W×H pair (e.g. "Default Title"), so the caller can fall back.
  //   - separator may be x, X or × with or without surrounding spaces
  //   - dimensions may be integer or decimal (comma or dot)
  //   - the unit and any extra option values are dropped; output is canonical
  // This generality is what lets non-standard sizes (e.g. Limited Edition
  // 50x100 / 40x50) reach the AR at the right scale instead of falling through
  // to the image-ratio guess below.
  function normalizeSize(sizeStr) {
    if (!sizeStr) return '';
    var s = String(sizeStr).trim();
    var aMatch = s.match(/A[1-4]\s*\(([^)]+)\)/i);
    if (aMatch && aMatch[1]) s = aMatch[1];
    var dim = s.match(/(\d+(?:[.,]\d+)?)\s*[x×X]\s*(\d+(?:[.,]\d+)?)/);
    if (!dim) return '';
    return dim[1] + 'x' + dim[2] + ' cm';
  }

  // ── Read selected size (three sources in priority order) ──────────────────
  // 1. localStorage — written by the variantChange bridge when the shopper
  //    picks a different size.
  // 2. <body data-selected-size> — the server-rendered initial selected variant
  //    (set in snippets/product.liquid). variantChange does NOT fire on load,
  //    so without this the first paint would always hit the fallback below.
  // 3. image-ratio fallback — only when neither source carries a parseable W×H.
  function readSelectedSize() {
    try {
      var nLS = normalizeSize(localStorage.getItem(SIZE_LS_KEY));
      if (nLS) return nLS;
    } catch (e) {}
    var nAttr = normalizeSize(document.body.getAttribute(SIZE_BRIDGE_ATTR));
    if (nAttr) return nAttr;
    return getDimensionDefault();
  }

  // ── Framed-preview compositor ──────────────────────────────────────────────
  // On Print-Configurator PDPs the "real" product image is the <print-preview>
  // composition: art + passepartout + frame stacked as layers (see
  // snippets/print-preview.liquid + assets/print-preview.css). Blankwall wants a
  // single image, so we flatten those layers onto a <canvas> and hand Blankwall
  // the canvas (its image getter accepts <canvas>, and with auto_crop it uploads
  // the pixels to its autocrop endpoint to obtain a hosted AR image). Falls back
  // to the gallery hero image when there is no <print-preview> or compositing
  // fails (e.g. a CORS-tainted canvas).
  var FRAME_CANVAS_ID = 'bw-frame-canvas';
  var FRAME = { canvas: null, ready: false };
  var state = { framed: false };

  function isFramedPDP() {
    return !!document.querySelector('print-preview');
  }

  // Whether this device actually launches AR (mirrors Blankwall's own capability
  // check: Android+Chrome → scene-viewer, iOS/iPadOS Safari → Quick Look).
  // Desktops only get a QR (which reopens this page on the phone, where the crop
  // runs anyway), so they skip the canvas/autocrop path — which would otherwise
  // make Blankwall's "BUILDING YOUR EXPERIENCE" loader flash for no benefit.
  function isArDevice() {
    var ua = navigator.userAgent || '';
    var androidChrome = /Chrome/i.test(ua) && /Android/i.test(ua);
    var iosSafari =
      /Safari/i.test(ua) &&
      (/iPhone|iPad|iPod/i.test(ua) ||
        (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1));
    return androidChrome || iosSafari;
  }

  function ensureFrameCanvas() {
    var c = document.getElementById(FRAME_CANVAS_ID);
    if (!c) {
      c = document.createElement('canvas');
      c.id = FRAME_CANVAS_ID;
      c.style.display = 'none';
      document.body.appendChild(c);
    }
    return c;
  }

  // Draw `img` into the layer's rect (fractions of the stage) with the same
  // object-fit the live <print-preview> uses ('contain' or 'cover'), clipped to
  // the rect. Mirrors print-preview.js / print-preview.css exactly.
  function drawInRect(ctx, img, rect, stageW, stageH) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    var bx = rect.x * stageW, by = rect.y * stageH;
    var bw = rect.w * stageW, bh = rect.h * stageH;
    var w, h;
    if (rect.fit === 'fill') { w = bw; h = bh; }
    else {
      var fit = rect.fit === 'cover' ? Math.max(bw / iw, bh / ih) : Math.min(bw / iw, bh / ih);
      w = iw * fit;
      h = ih * fit;
    }
    ctx.save();
    ctx.beginPath();
    if (rect.clip) ctx.rect(rect.clip.x * stageW, rect.clip.y * stageH, rect.clip.w * stageW, rect.clip.h * stageH);
    else ctx.rect(bx, by, bw, bh);
    ctx.clip();
    ctx.drawImage(img, bx + (bw - w) / 2, by + (bh - h) / 2, w, h);
    ctx.restore();
  }

  function loadCorsImage(src, cb) {
    if (!src) { cb(null); return; }
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () { cb(im); };
    im.onerror = function () { cb(null); };
    // Cache-bust so the browser issues a fresh CORS request instead of reusing a
    // non-CORS cached response (which would taint the canvas on export).
    im.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'bwcors=1';
  }

  function composeFramed(cb) {
    cb = cb || function () {};
    var pp = document.querySelector('print-preview');
    if (!pp) { state.framed = false; cb(false); return; }

    var inner = pp.querySelector('[data-pdp-print-preview]');
    var artEl = pp.querySelector('[data-pdp-print-art]');
    var passeEl = pp.querySelector('[data-pdp-print-passepartout]');
    var frameEl = pp.querySelector('[data-pdp-print-frame]');

    function visibleSrc(el) {
      if (!el || el.hasAttribute('hidden')) return '';
      return el.currentSrc || el.getAttribute('src') || '';
    }
    var artSrc = visibleSrc(artEl);
    var passeSrc = visibleSrc(passeEl);
    var frameSrc = visibleSrc(frameEl);
    if (!artSrc) { state.framed = false; cb(false); return; }

    // Mirror <print-preview>'s MEASURED composition (print-preview.js): the
    // stage ratio comes from `--stage-aspect` and every layer carries its rect
    // as inline percentage left/top/width/height within the stage. Nothing
    // about orientation or asset canvases is assumed here.
    var stageAspect =
      (inner && parseFloat(getComputedStyle(inner).getPropertyValue('--stage-aspect'))) || 800 / 1100;

    var LONG = 1600;
    var cw, ch;
    if (stageAspect >= 1) { cw = LONG; ch = Math.round(LONG / stageAspect); }
    else { ch = LONG; cw = Math.round(LONG * stageAspect); }

    function layerRect(el) {
      var pct = function (v, d) { var n = parseFloat(v); return isNaN(n) ? d : n / 100; };
      var clip = null;
      var raw = el.getAttribute('data-clip');
      if (raw) {
        var parts = raw.split(',').map(parseFloat);
        if (parts.length === 4 && !parts.some(isNaN)) clip = { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
      }
      return {
        x: pct(el.style.left, 0),
        y: pct(el.style.top, 0),
        w: pct(el.style.width, 1),
        h: pct(el.style.height, 1),
        fit: el.style.objectFit || getComputedStyle(el).objectFit || 'contain',
        clip: clip, // stage-fraction rect the layer is clipped to (art under a mat)
      };
    }

    var layers = [{ src: artSrc, rect: layerRect(artEl) }];
    if (passeSrc) layers.push({ src: passeSrc, rect: layerRect(passeEl) });
    var frameIdx = -1;
    if (frameSrc) {
      layers.push({ src: frameSrc, rect: layerRect(frameEl) });
      frameIdx = layers.length - 1;
    }

    var imgs = new Array(layers.length);
    var pending = layers.length;
    layers.forEach(function (L, i) {
      loadCorsImage(L.src, function (im) {
        imgs[i] = im;
        if (--pending === 0) paint();
      });
    });

    function paint() {
      // Composite on a TRANSPARENT temp canvas (no backdrop). Blankwall's
      // autocrop only trims white, not our old #ebebeb backdrop, so instead of
      // relying on it we crop to the artwork ourselves below. Any transparent
      // pixels Blankwall later flattens to white for its JPEG export.
      var tmp = document.createElement('canvas');
      tmp.width = cw;
      tmp.height = ch;
      var tctx = tmp.getContext('2d');
      for (var i = 0; i < layers.length; i++) {
        if (imgs[i]) drawInRect(tctx, imgs[i], layers[i].rect, cw, ch);
      }

      // Read pixels (also surfaces a CORS taint via the thrown SecurityError)
      // and find the bounding box of non-transparent pixels — i.e. the frame's
      // outer edge — so we can crop the backdrop away entirely.
      // Decide what defines the crop edge. Prefer the FRAME layer's solid
      // pixels so we crop to the frame's outer edge — not a passepartout/mat
      // that extends past it, nor the frame PNG's soft outer drop shadow. The
      // high alpha cutoff (200) keeps the opaque frame and drops the shadow.
      // With no frame selected, crop to whatever was drawn (alpha > 8).
      var boxCtx, cutoff;
      if (frameIdx >= 0 && imgs[frameIdx]) {
        var fc = document.createElement('canvas');
        fc.width = cw;
        fc.height = ch;
        boxCtx = fc.getContext('2d');
        drawInRect(boxCtx, imgs[frameIdx], layers[frameIdx].rect, cw, ch);
        cutoff = 200;
      } else {
        boxCtx = tctx;
        cutoff = 8;
      }

      var px;
      try { px = boxCtx.getImageData(0, 0, cw, ch).data; }
      catch (e) {
        debug('composeFramed: canvas tainted, falling back to hero image', e);
        state.framed = false;
        FRAME.ready = false;
        cb(false);
        return;
      }

      var minX = cw, minY = ch, maxX = -1, maxY = -1;
      for (var y = 0; y < ch; y++) {
        var row = y * cw * 4;
        for (var x = 0; x < cw; x++) {
          if (px[row + x * 4 + 3] > cutoff) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < minX || maxY < minY) {
        debug('composeFramed: nothing composited, falling back to hero image');
        state.framed = false;
        FRAME.ready = false;
        cb(false);
        return;
      }
      var ow = maxX - minX + 1;
      var oh = maxY - minY + 1;

      var canvas = ensureFrameCanvas();
      canvas.width = ow;
      canvas.height = oh;
      var ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, ow, oh);
      ctx.drawImage(tmp, minX, minY, ow, oh, 0, 0, ow, oh);

      FRAME.canvas = canvas;
      FRAME.ready = true;
      state.framed = true;
      debug('composeFramed: canvas ready (cropped)', ow + 'x' + oh, layers.length + ' layer(s)');
      cb(true);
    }
  }

  var _recomposeT = null;
  function bindRecompose() {
    var root = document.querySelector('product-info') || document;
    // print-preview.js fires this after every (re)composition — including the
    // async one once it has measured a newly selected PNG — so we always read
    // final layer rects. Debounced to coalesce the sync + async passes.
    root.addEventListener('print-preview:composed', function () {
      clearTimeout(_recomposeT);
      _recomposeT = setTimeout(function () { composeFramed(); }, 150);
    });
  }

  // Plain (non-configurator) products: the product image is the poster shot on
  // a uniform mockup backdrop (e.g. a white sheet on light grey). Blankwall's
  // <img> path would send that whole shot — backdrop and all — and its autocrop
  // only trims white, not the grey backdrop. So we crop the backdrop off here
  // and feed Blankwall a canvas. Strictly gated: only images whose four corners
  // are the same uniform colour are treated as mockups; anything else (lifestyle
  // shots, full-bleed art) bails to the original untouched <img> behaviour.
  function composePlain(cb) {
    cb = cb || function () {};
    var hero = firstHeroImage();
    if (!hero || !hero.src) { if (!FRAME.ready) state.framed = false; cb(false); return; }

    loadCorsImage(normalizeImageUrl(hero.src), function (im) {
      if (!im) { if (!FRAME.ready) state.framed = false; cb(false); return; }
      var cw = im.naturalWidth || im.width;
      var ch = im.naturalHeight || im.height;
      if (!cw || !ch) { if (!FRAME.ready) state.framed = false; cb(false); return; }

      var tmp = document.createElement('canvas');
      tmp.width = cw;
      tmp.height = ch;
      var tctx = tmp.getContext('2d');
      tctx.drawImage(im, 0, 0);

      var px;
      try { px = tctx.getImageData(0, 0, cw, ch).data; }
      catch (e) {
        debug('composePlain: canvas tainted, using original image', e);
        if (!FRAME.ready) state.framed = false;
        cb(false);
        return;
      }

      function corner(sx, sy) {
        var r = 0, g = 0, b = 0, n = 0;
        for (var y = sy; y < sy + 6 && y < ch; y++) {
          for (var x = sx; x < sx + 6 && x < cw; x++) {
            var o = (y * cw + x) * 4;
            r += px[o]; g += px[o + 1]; b += px[o + 2]; n++;
          }
        }
        return n ? [r / n, g / n, b / n] : [233, 233, 233];
      }
      var c = [corner(0, 0), corner(cw - 6, 0), corner(0, ch - 6), corner(cw - 6, ch - 6)];
      function diff(a, b) { return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]); }
      // Only treat as a mockup if all four corners are the same backdrop colour.
      if (diff(c[0], c[1]) > 30 || diff(c[0], c[2]) > 30 || diff(c[0], c[3]) > 30) {
        debug('composePlain: non-uniform backdrop, using original image');
        if (!FRAME.ready) state.framed = false;
        cb(false);
        return;
      }
      var bgR = (c[0][0] + c[1][0] + c[2][0] + c[3][0]) / 4;
      var bgG = (c[0][1] + c[1][1] + c[2][1] + c[3][1]) / 4;
      var bgB = (c[0][2] + c[1][2] + c[2][2] + c[3][2]) / 4;
      var bgLuma = 0.299 * bgR + 0.587 * bgG + 0.114 * bgB;

      // Foreground = lighter than the backdrop (the white sheet) OR colourful
      // (the artwork). The soft drop shadow is darker + grey, so it's excluded.
      var minX = cw, minY = ch, maxX = -1, maxY = -1;
      for (var y = 0; y < ch; y++) {
        var rowo = y * cw * 4;
        for (var x = 0; x < cw; x++) {
          var o2 = rowo + x * 4;
          var R = px[o2], G = px[o2 + 1], B = px[o2 + 2];
          var luma = 0.299 * R + 0.587 * G + 0.114 * B;
          var sat = Math.max(R, G, B) - Math.min(R, G, B);
          if (luma > bgLuma + 10 || sat > 28) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < minX || maxY < minY) {
        debug('composePlain: nothing distinct from backdrop, using original image');
        if (!FRAME.ready) state.framed = false;
        cb(false);
        return;
      }

      var ow = maxX - minX + 1;
      var oh = maxY - minY + 1;
      var canvas = ensureFrameCanvas();
      canvas.width = ow;
      canvas.height = oh;
      canvas.getContext('2d').drawImage(tmp, minX, minY, ow, oh, 0, 0, ow, oh);

      FRAME.canvas = canvas;
      FRAME.ready = true;
      state.framed = true;
      debug('composePlain: cropped', ow + 'x' + oh, 'bgLuma', Math.round(bgLuma));
      cb(true);
    });
  }

  function bindPlainResync() {
    var sub = window.subscribe;
    var ev = window.theme && window.theme.PUB_SUB_EVENTS;
    if (!sub || !ev || !ev.variantChange) return;
    sub(ev.variantChange, function () {
      clearTimeout(_recomposeT);
      _recomposeT = setTimeout(function () { composePlain(); }, 200);
    });
  }

  // ── DOM element helpers ───────────────────────────────────────────────────
  function ensureHiddenImg() {
    var img = document.querySelector('img.product-image');
    if (!img) {
      img = document.createElement('img');
      img.className = 'product-image';
      img.alt = '';
      img.style.display = 'none';
      img.dataset.blankwallSource = 'js';
      document.body.appendChild(img);
    } else {
      // Server-provided image: leave as-is
      if (img.dataset.blankwallSource === 'vm' && img.src) return img;
    }
    var hero = firstHeroImage();
    if (hero && hero.src) {
      var normalized = normalizeImageUrl(hero.src);
      if (img.src !== normalized) {
        debug('ensureHiddenImg: updating src', normalized);
        img.src = normalized;
      }
      img.dataset.blankwallSource = hero.source || 'js';
    }
    return img;
  }

  function ensureDims() {
    var dims = document.querySelector('.product-description');
    if (!dims) {
      dims = document.createElement('div');
      dims.className = 'product-description';
      dims.style.display = 'none';
      document.body.appendChild(dims);
    }
    dims.textContent = readSelectedSize();
    return dims;
  }

  function ensureContainer() {
    var container = document.getElementById('blankwall-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'blankwall-container';
      container.style.marginTop = '1rem';
      document.body.appendChild(container);
      debug('ensureContainer: created #blankwall-container');
    }
    return container;
  }

  // ── Blankwall.init ────────────────────────────────────────────────────────
  function initIfReady() {
    var framed = state.framed && FRAME.ready && document.getElementById(FRAME_CANVAS_ID);
    var imageEl = framed
      ? document.getElementById(FRAME_CANVAS_ID)
      : document.querySelector('img.product-image[src]');
    var ok =
      window.Blankwall &&
      document.querySelector('#blankwall-container') &&
      imageEl &&
      document.querySelector('.product-description');
    if (!ok) return false;

    var isMobile =
      window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
    var style = isMobile
      ? {
          buttonText: 'View in Your Room',
          width: '100%',
          padding: '12px 16px',
          borderRadius: '0px'
        }
      : { buttonText: 'View in Your Room' };

    window.Blankwall.init(
      {
        ar_container_selector: '#blankwall-container',
        ar_image_selector: framed ? '#' + FRAME_CANVAS_ID : 'img.product-image',
        ar_dimensions_selector: '.product-description',
        // Canvas sources have no hosted URL, so Blankwall must upload the pixels
        // to its autocrop endpoint to obtain an AR image; auto_crop also trims
        // the #ebebeb backdrop down to the framed artwork.
        auto_crop: framed ? true : false
      },
      style
    );
    debug('Blankwall.init called');
    return true;
  }

  // ── Sync routines ─────────────────────────────────────────────────────────
  // Keeps img.product-image in sync with gallery navigation changes, and
  // .product-description in sync with the selected size.
  function startSync() {
    debug('startSync: setting up sync routines', state.framed ? '(framed)' : '(hero)');

    // Size sync runs in every mode (keeps .product-description current).
    var sizeIv = setInterval(function () {
      var dims = document.querySelector('.product-description');
      if (!dims) return;
      var val = readSelectedSize();
      if (dims.textContent !== val) dims.textContent = val;
    }, 600);

    // Framed mode: the AR source is the composited canvas, refreshed on
    // configurator changes (bindRecompose). No gallery-hero image syncing.
    if (state.framed) {
      window.addEventListener('unload', function () { clearInterval(sizeIv); });
      return;
    }

    var prod = document.querySelector('img.product-image');

    var updateImg = function () {
      var hero = firstHeroImage();
      if (hero && hero.src) {
        var clean = normalizeImageUrl(hero.src);
        if (clean && prod && prod.src !== clean) {
          debug('startSync.updateImg: updating product image', clean);
          prod.src = clean;
        }
      }
    };
    updateImg();

    var target =
      document.querySelector('.product__slides') ||
      document.querySelector('.product-single__photos') ||
      document.body;

    if (window.MutationObserver) {
      var obs = new MutationObserver(function () {
        updateImg();
      });
      obs.observe(target, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['src', 'class']
      });
      window.addEventListener('unload', function () {
        obs.disconnect();
        clearInterval(sizeIv);
        debug('startSync: cleanup');
      });
    } else {
      var iv = setInterval(updateImg, 1000);
      window.addEventListener('unload', function () {
        clearInterval(iv);
        clearInterval(sizeIv);
      });
    }
  }

  // ── Boot sequence ─────────────────────────────────────────────────────────
  function boot() {
    var arDevice = isArDevice();
    debug('boot: creating required DOM elements', arDevice ? '(AR device)' : '(desktop)');
    ensureContainer();
    ensureDims();

    if (!arDevice) {
      // Desktop: keep the original instant <img> path — no autocrop, no
      // "BUILDING YOUR EXPERIENCE" flash. The QR it shows reopens this page on
      // the phone, where the crop runs.
      ensureHiddenImg();
    } else if (isFramedPDP()) {
      // Optimistically take the framed path; composeFramed() resets
      // state.framed to false if it can't build an exportable canvas, and the
      // interval below then falls back to the gallery hero image.
      state.framed = true;
      ensureFrameCanvas();
      composeFramed();
      bindRecompose();
    } else {
      // Plain product: try cropping the mockup backdrop off the product image
      // and feed Blankwall a canvas. composePlain() leaves state.framed=false
      // for non-mockup images / CORS failures, and the interval below then
      // restores the original untouched <img> behaviour.
      state.framed = true;
      ensureFrameCanvas();
      composePlain();
      bindPlainResync();
    }

    var tries = 0;
    var iv = setInterval(function () {
      ensureDims();
      if (!state.framed) ensureHiddenImg();
      if (initIfReady() || ++tries > 40) {
        clearInterval(iv);
        startSync();
      }
    }, 250);
  }

  function bindViewInRoomButton() {
    // There can be two buttons on a PDP (the mobile overlay and the desktop
    // accordion row); only one is visible per breakpoint, but bind them all.
    var btns = document.querySelectorAll('.product__view-in-room');
    if (!btns.length) return;
    function triggerAr() {
      var arBtn = document.querySelector('#blankwall-container .ar-button');
      if (arBtn) { arBtn.click(); return; }
      // Blankwall not yet initialised — wait briefly and retry once.
      setTimeout(function () {
        var arBtnDelayed = document.querySelector('#blankwall-container .ar-button');
        if (arBtnDelayed) arBtnDelayed.click();
      }, 800);
    }
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', triggerAr);
    }
  }

  function init() {
    bindVariantSizeBridge();
    bindViewInRoomButton();
    loadBlankwall(function () {
      setTimeout(boot, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
